import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/Dialog';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Plus, Folder, Trash2, Edit2, X, Check } from 'lucide-react';
import { usePersistenceStore } from '../store/persistenceStore';
import { useToast } from '../contexts/ToastContext';
import { Project } from '../types/persistence';

interface ProjectManagerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectProject: (project: Project) => void;
}

export const ProjectManager: React.FC<ProjectManagerProps> = ({
  isOpen,
  onClose,
  onSelectProject
}) => {
  const { 
    projects, 
    loadProjects, 
    createProject, 
    updateProject, 
    deleteProject,
    isLoading,
    error,
    clearError
  } = usePersistenceStore();
  
  const { toast } = useToast();
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [editingProject, setEditingProject] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadProjects().catch(err => {
        toast({
          title: 'Failed to load projects',
          description: err.message,
          variant: 'error'
        });
      });
    }
  }, [isOpen, loadProjects, toast]);

  useEffect(() => {
    if (error) {
      toast({
        title: 'Error',
        description: error,
        variant: 'error'
      });
      clearError();
    }
  }, [error, clearError, toast]);

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) {
      toast({
        title: 'Invalid name',
        description: 'Project name cannot be empty',
        variant: 'error'
      });
      return;
    }

    try {
      const project = await createProject(newProjectName, newProjectDescription);
      toast({
        title: 'Project created',
        description: `Created project "${project.name}"`,
        variant: 'success'
      });
      setShowNewProject(false);
      setNewProjectName('');
      setNewProjectDescription('');
      onSelectProject(project);
    } catch (err) {
      // Error handled by store
    }
  };

  const handleUpdateProject = async (projectId: string) => {
    if (!editName.trim()) {
      toast({
        title: 'Invalid name',
        description: 'Project name cannot be empty',
        variant: 'error'
      });
      return;
    }

    try {
      await updateProject(projectId, {
        name: editName,
        description: editDescription
      });
      toast({
        title: 'Project updated',
        description: 'Project details have been updated',
        variant: 'success'
      });
      setEditingProject(null);
    } catch (err) {
      // Error handled by store
    }
  };

  const handleDeleteProject = async (project: Project) => {
    if (!window.confirm(`Are you sure you want to delete "${project.name}"? This will delete all graphs in the project.`)) {
      return;
    }

    try {
      await deleteProject(project.id);
      toast({
        title: 'Project deleted',
        description: `Deleted project "${project.name}"`,
        variant: 'success'
      });
    } catch (err) {
      // Error handled by store
    }
  };

  const startEditing = (project: Project) => {
    setEditingProject(project.id);
    setEditName(project.name);
    setEditDescription(project.description || '');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Project Manager</DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto">
          {/* New Project Form */}
          {showNewProject ? (
            <div className="mb-4 p-4 border rounded-lg bg-gray-50 dark:bg-gray-800">
              <h3 className="font-semibold mb-3">Create New Project</h3>
              <Input
                placeholder="Project name"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                className="mb-2"
                autoFocus
              />
              <Input
                placeholder="Description (optional)"
                value={newProjectDescription}
                onChange={(e) => setNewProjectDescription(e.target.value)}
                className="mb-3"
              />
              <div className="flex gap-2">
                <Button 
                  onClick={handleCreateProject} 
                  disabled={isLoading}
                  size="sm"
                >
                  Create
                </Button>
                <Button 
                  variant="ghost" 
                  onClick={() => {
                    setShowNewProject(false);
                    setNewProjectName('');
                    setNewProjectDescription('');
                  }}
                  size="sm"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button
              onClick={() => setShowNewProject(true)}
              className="mb-4 w-full"
              variant="outline"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Project
            </Button>
          )}

          {/* Projects List */}
          <div className="space-y-2">
            {projects.length === 0 && !isLoading && (
              <p className="text-center text-gray-500 py-8">
                No projects yet. Create your first project to get started.
              </p>
            )}
            
            {projects.map(project => (
              <div
                key={project.id}
                className="p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                {editingProject === project.id ? (
                  <div>
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="mb-2"
                      autoFocus
                    />
                    <Input
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      placeholder="Description (optional)"
                      className="mb-2"
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleUpdateProject(project.id)}
                        disabled={isLoading}
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditingProject(null)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between">
                    <div 
                      className="flex-1 cursor-pointer"
                      onClick={() => onSelectProject(project)}
                    >
                      <div className="flex items-center gap-2">
                        <Folder className="w-5 h-5 text-blue-500" />
                        <h3 className="font-semibold">{project.name}</h3>
                      </div>
                      {project.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 ml-7">
                          {project.description}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-2 ml-7">
                        Updated {new Date(project.updated_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          startEditing(project);
                        }}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteProject(project);
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};